import * as React from 'react';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import { createStyles, makeStyles } from '@material-ui/core';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import {
  getNodeConfigAssetNameList,
  getConfigValues,
} from '../../../store/Config/Settings/ConfigSettingsActions';
import { CategoryCountRequest } from '../../../Interfaces/Assets/CategoryCountRequest';
import { GetString } from '../../StringResourceManager';
import DynamicComboBox from '../../DynamicComboBox';
import { FilterDescriptor } from '@progress/kendo-data-query';
import { ApiEnums } from '../../../Interfaces/ApiEnums';

const useStyles = makeStyles(() =>
  createStyles({
    assetCategoryInput: { width: '250px', minWidth: '5%' },
    assetNameInput: { width: '250px', minWidth: '5%', marginLeft: 10 },
    assetValueTextInput: {
      marginLeft: 10,
    },
  }),
);

interface Props {
  filter: FilterDescriptor;
  onFilterChange: (event: { nextFilter: FilterDescriptor }) => void;
  configSettingType: ApiEnums.ConfigSettingsType;
}

export const CustomFilterNodeConfigInfo: React.FC<Props> = ({
  filter,
  onFilterChange,
  configSettingType,
}) => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const assetCategories: string[] = useSelector(
    (state: ApplicationState) => state.configSettingsReducer.assetCategories,
  );

  const assetNames: Map<string, string[]> = useSelector(
    (state: ApplicationState) => state.configSettingsReducer.assetNames,
  );

  const configValues: Map<CategoryCountRequest, string[]> = useSelector(
    (state: ApplicationState) => state.configSettingsReducer.configValues,
  );

  const isFetchingConfigValues: boolean = useSelector(
    (state: ApplicationState) =>
      state.configSettingsReducer.isFetchingConfigValues,
  );

  const isFetchingAssetCategories: boolean = useSelector(
    (state: ApplicationState) =>
      state.configSettingsReducer.isFetchingAssetCategories,
  );

  const isFetchingAssetNames: boolean = useSelector(
    (state: ApplicationState) =>
      state.configSettingsReducer.isFetchingAssetNames,
  );

  React.useEffect(() => {
    const categoryFilter = filter?.value?.Category;
    const nameFilter = filter?.value?.Name;

    if (categoryFilter !== undefined) {
      getNodeConfigAssetNameList(dispatch, configSettingType, categoryFilter);
    }

    if (nameFilter !== undefined) {
      processFetchCategoryCounts(nameFilter);
    }
  }, [assetCategories]);

  const onChangeAssetCategory = (event: any) => {
    if (!assetNames.has(event.target.value)) {
      getNodeConfigAssetNameList(
        dispatch,
        configSettingType,
        event.target.value,
      );
    }
    onFilterChange({
      nextFilter: {
        ...filter,
        value: {
          Category: event.target.value,
          Name: undefined,
          Value: undefined,
        },
      },
    });
  };

  const processFetchCategoryCounts = (name: string | undefined) => {
    let categoryCountRequest: CategoryCountRequest = {
      Category: filter?.value?.Category,
      Name: name ?? '',
    };

    getConfigValues(dispatch, configSettingType, categoryCountRequest);
  };

  const onChangeAssetName = (event: any) => {
    processFetchCategoryCounts(event.target.value);

    onFilterChange({
      nextFilter: {
        ...filter,
        value: {
          Category: filter.value?.Category ?? '',
          Name: event.target.value,
          Value: undefined,
        },
      },
    });
  };

  const onValueTextChange = (event: any) => {
    onFilterChange({
      nextFilter: {
        ...filter,
        value: {
          Category: filter.value?.Category ?? '',
          Name: filter.value?.Name ?? '',
          Value: event.target.value,
        },
      },
    });
  };

  const getNodeConfigData = (category: string, name: string) => {
    let categoryCountRequest: CategoryCountRequest = {
      Category: category,
      Name: name,
    };

    var values: any = [];
    configValues.forEach(function (value: any, key: any, map: any) {
      if (
        key.Category == categoryCountRequest.Category &&
        key.Name == categoryCountRequest.Name
      ) {
        values = value;
      }
    });

    return values;
  };

  return (
    <React.Fragment>
      <DynamicComboBox
        className={classes.assetCategoryInput}
        onChange={onChangeAssetCategory}
        data={assetCategories}
        value={filter?.value?.Category ?? ''}
        placeholder={GetString('CATEGORY_PLACEHOLDER')}
        loading={isFetchingAssetCategories}
      />
      <DynamicComboBox
        className={classes.assetNameInput}
        onChange={onChangeAssetName}
        data={assetNames.get(filter?.value?.Category)}
        value={filter?.value?.Name ?? ''}
        placeholder={GetString('NAME_PLACEHOLDER')}
        disabled={!filter?.value?.Category}
        loading={isFetchingAssetNames}
      />
      <DynamicComboBox
        className={classes.assetValueTextInput}
        placeholder={GetString('VALUE_PLACEHOLDER')}
        onChange={onValueTextChange}
        data={getNodeConfigData(filter?.value?.Category, filter?.value?.Name)}
        value={filter?.value?.Value ?? ''}
        disabled={!filter?.value?.Name}
        loading={isFetchingConfigValues}
      />
    </React.Fragment>
  );
};
